import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { dotmapsGray } from '@constants/colors';

// Replaces material-ui/styles/transitions.easeOut()
const easeOutTransition = 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms';

// A material-ui based data-uri icon (based on material-ui SvgIcon class).
const DataUriIcon = ({
  children,
  color,
  data,
  hoverColor,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  style = {},
  ...other
}) => {
  const [hovered, setHovered] = useState(false);

  const handleMouseLeave = useCallback((event) => {
    setHovered(false);
    onMouseLeave(event);
  }, [onMouseLeave]);

  const handleMouseEnter = useCallback((event) => {
    setHovered(true);
    onMouseEnter(event);
  }, [onMouseEnter]);

  const offColor = color || 'currentColor';
  const onColor = hoverColor || offColor;

  const mergedStyles = {
    display: 'inline-block',
    backgroundImage: `url(data:image/png;base64,${data})`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${style.height || 24}px ${style.width || 24}px`,
    color: dotmapsGray,
    fill: hovered ? onColor : offColor,
    height: 24,
    width: 24,
    userSelect: 'none',
    transition: easeOutTransition,
    ...style
  };

  return (
    <div
      data-testid="data-uri-icon"
      {...other}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={mergedStyles}
    >
      {children}
    </div>
  );
};

DataUriIcon.propTypes = {
  /**
   * Elements passed into the PNG Icon.
   */
  children: PropTypes.node,
  /**
   * This is the fill color of the PNG icon.
   * If not specified, this component will default
   * to muiTheme.palette.textColor.
   */
  color: PropTypes.string,
  /**
   * The base64 encoded PNG image.
   */
  data: PropTypes.string,
  /**
   * This is the icon color when the mouse hovers over the icon.
   */
  hoverColor: PropTypes.string,
  /** @ignore */
  onMouseEnter: PropTypes.func,
  /** @ignore */
  onMouseLeave: PropTypes.func,
  /**
   * Override the inline-styles of the root element.
   */
  style: PropTypes.object
};

export default DataUriIcon;
